import React, { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import CircleAnimation from 'src/Component/CircleAnimation'
import SpeedCombineData from 'src/Home/SpeedMeter/SpeedCombineData'
import { DarkButton } from 'src/Component/ThemedButton'
import { SpeedTestState } from 'src/Common/SpeedTestState'
import { useTheme } from 'src/Styles/theme'
import { Route } from 'src/Common/Route'
import { ReactComponent as SpeedTestLogo } from 'images/speed-test-logo.svg'
import { SortedServer } from 'src/lib/ookla_speed_test'
import { analytics } from 'src/Analytics'
import {
  RunSpeedCheckTappedEvent,
  SpeedTestCompletedEvent,
} from 'src/Analytics/SpeedTestEvents'
import { Body1 } from 'src/Styles/text-styles'
import { localize } from 'src/lib/localize'

const Container = styled.div`
  flex: 1;
  padding-top: 16px;
  @media (min-width: 768px) {
    margin-right: 180px;
    margin-left: 180px;
  }

  @media (min-width: 992px) {
    margin-right: 320px;
    margin-left: 320px;
  }

  @media (min-width: 1200px) {
    margin-right: 480px;
    margin-left: 480px;
  }
`
const BtnContainer = styled.div`
  @media (min-width: 320px) {
    padding-right: 48px;
    padding-left: 48px;
    padding-top: 16px;
  }

  @media (min-width: 992px) {
    padding-right: 64px;
    padding-left: 64px;
    padding-top: 42px;
  }

  @media (min-width: 1265px) {
    padding-right: 72px;
    padding-left: 72px;
    padding-top: 42px;
  }

  @media (min-width: 1365px) {
    padding-right: 84px;
    padding-left: 84px;
    padding-top: 42px;
  }
`

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 24px;
  padding-bottom: 32px;
  @media (max-width: 768px) {
    padding-bottom: 12px;
  }
`

const SpeedCombineDataContainer = styled.div`
  margin-left: 32px;
  margin-right: 32px;
`

const FindingOptimalServerView = styled(Body1)`
  text-align: center;
  @media (max-width: 768px) {
    padding-top: 16px;
    font-size: 12px;
  }

  @media (min-width: 768px) {
    padding-top: 40px;
    font-size: 20px;
  }
`

const RunSpeedTestBtnText = localize('BUTTON')('Run Speed Check')
const FindingOptimalServerText = localize('LABEL')('Finding optimal server...')

interface Props {
  testState: SpeedTestState
  startTest(): void
  latency: number
  download: number
  upload: number
  progress: number
  sortedServer: SortedServer | undefined
  clientIP: string | undefined
  guid: string | undefined
  isOptimalLocationLoaded: boolean
}

const SpeedMeter = ({
  testState,
  startTest,
  latency,
  download,
  upload,
  progress,
  sortedServer,
  clientIP,
  guid,
  isOptimalLocationLoaded,
}: Props) => {
  const { progressStokeColor } = useTheme().colors
  const history = useHistory()

  useEffect(() => {
    switch (testState) {
      case SpeedTestState.COMPLETED:
        history.push(Route.RESULT, {
          result: {
            download: download,
            upload: upload,
            latency: latency,
            server_host: sortedServer?.sponsor,
            provider_name: '',
            city_name: sortedServer?.name,
            country_name: sortedServer?.country,
            client_ip: clientIP,
            result_guid: guid,
          },
        })
        analytics.trackEvent(
          SpeedTestCompletedEvent(
            download,
            upload,
            latency,
            sortedServer?.name,
            sortedServer?.sponsor,
            clientIP,
            guid
          )
        )
        break
    }
  })

  const startSpeedTest = () => {
    startTest()
    analytics.trackEvent(RunSpeedCheckTappedEvent())
  }
  return (
    <Container>
      <CircleAnimation
        borderStrokeColor={progressStokeColor}
        progress={progress}
        speed={
          download >= 50 ? 2 : download <= 5 ? 1.5 : download === 0 ? 0 : 1
        }
        isPaused={
          !(
            testState === SpeedTestState.DOWNLOADING ||
            testState === SpeedTestState.UPLOADING
          )
        }
      />
      <SpeedCombineDataContainer>
        <SpeedCombineData
          download={download.toString()}
          upload={upload.toString()}
          ping={latency.toString()}
          testState={testState}
        />
      </SpeedCombineDataContainer>

      {!isOptimalLocationLoaded && (
        <FindingOptimalServerView>
          {FindingOptimalServerText}
        </FindingOptimalServerView>
      )}
      {testState === SpeedTestState.IDEAL && (
        <BtnContainer
          style={{ pointerEvents: !isOptimalLocationLoaded ? 'none' : 'auto' }}
        >
          <DarkButton
            onPress={startSpeedTest}
            title={RunSpeedTestBtnText}
            disabled={!isOptimalLocationLoaded}
          />
        </BtnContainer>
      )}

      <LogoContainer>
        <SpeedTestLogo />
      </LogoContainer>
    </Container>
  )
}

export default SpeedMeter
